import { CAD_GEOREF_MIN_POINTS } from '../data/cadConstants';
import {
  CAD_PLAN_METERS_PER_DEGREE,
  computeFeatureCollectionBbox,
  isLikelyWgs84,
  mapCadFeatureCollection,
  normalizeCadFeatureCollection,
  parseCadBounds,
  readCadPlanBounds,
} from './cadGeometry';

/**
 * @param {number} lat
 * @returns {number}
 */
export function metersPerDegreeLng(lat) {
  const phi = (Number(lat) * Math.PI) / 180;
  return CAD_PLAN_METERS_PER_DEGREE * Math.max(Math.cos(phi), 0.2);
}

/**
 * Parcelas en metros DXF (canvas). Null si ya son WGS84.
 * @param {object|null|undefined} project
 * @returns {object|null}
 */
export function readCadMeterCollection(project) {
  const features = Array.isArray(project?.parcels?.features)
    ? project.parcels.features
    : [];
  const withGeom = features.filter((feature) => feature?.geometry);
  if (withGeom.length === 0) return null;
  if (withGeom.every((feature) => isLikelyWgs84(feature.geometry))) return null;
  return project.parcels;
}

function isFinitePair(pair) {
  return Array.isArray(pair)
    && pair.length >= 2
    && Number.isFinite(Number(pair[0]))
    && Number.isFinite(Number(pair[1]));
}

function isCadSrcMetersPair(pair) {
  if (!isFinitePair(pair)) return false;
  return !isLikelyWgs84({ coordinates: [Number(pair[0]), Number(pair[1])] });
}

/**
 * Tres esquinas fijas del DXF (metros). Mismo orden que el GET:
 * `[[minX,minY],[maxX,minY],[minX,maxY]]`.
 * @param {unknown} raw
 * @returns {number[][]|null}
 */
export function parseCadGeorefSrcCorners(raw) {
  if (!Array.isArray(raw) || raw.length < CAD_GEOREF_MIN_POINTS) return null;
  const corners = raw.slice(0, 3).map((pair) => (
    isCadSrcMetersPair(pair) ? [Number(pair[0]), Number(pair[1])] : null
  ));
  return corners.every(Boolean) ? corners : null;
}

export function srcBoundsFromCadSrcCorners(corners) {
  const parsed = parseCadGeorefSrcCorners(corners);
  if (!parsed) return null;
  const xs = parsed.map((pair) => pair[0]);
  const ys = parsed.map((pair) => pair[1]);
  return {
    minX: Math.min(...xs),
    minY: Math.min(...ys),
    maxX: Math.max(...xs),
    maxY: Math.max(...ys),
  };
}

function srcCornersFromBounds(srcBounds) {
  if (!srcBounds) return null;
  const minX = Number(srcBounds.minX);
  const minY = Number(srcBounds.minY);
  const maxX = Number(srcBounds.maxX);
  const maxY = Number(srcBounds.maxY);
  if (![minX, minY, maxX, maxY].every(Number.isFinite)) return null;
  if (!isCadSrcMetersPair([minX, minY]) || !isCadSrcMetersPair([maxX, maxY])) return null;
  return [
    [minX, minY],
    [maxX, minY],
    [minX, maxY],
  ];
}

/**
 * Esquinas en metros para el PATCH y para pintar la imagen base.
 * GET manage: `georef_src_corners`. Editor/visitante: a menudo faltan; el
 * `src` de `georef.points` o `canvas_image.bounds` (metros DXF) alcanza.
 * No usa `project.bounds` WGS84 del modo map.
 * @param {object|null|undefined} project
 * @returns {number[][]|null}
 */
export function readCadGeorefSrcCorners(project) {
  const fromGet = parseCadGeorefSrcCorners(project?.georef_src_corners);
  if (fromGet) return fromGet;
  const fromPoints = parseCadGeorefSrcCorners(
    (Array.isArray(project?.georef?.points) ? project.georef.points : [])
      .map((point) => point?.src),
  );
  if (fromPoints) return fromPoints;
  const meters = readCadMeterCollection(project);
  if (meters) {
    const fromMeters = srcCornersFromBounds(readCadPlanBounds(project, meters));
    if (fromMeters) return fromMeters;
  }
  const fromImage = srcCornersFromBounds(parseCadBounds(project?.canvas_image?.bounds));
  if (fromImage) return fromImage;
  return srcCornersFromBounds(parseCadBounds(project?.bounds));
}

function stickerSrcCorners(sticker) {
  return parseCadGeorefSrcCorners(sticker?.srcCorners)
    || srcCornersFromBounds(sticker?.srcBounds);
}

function isFiniteSticker(sticker) {
  return Boolean(sticker)
    && Number.isFinite(Number(sticker.lng))
    && Number.isFinite(Number(sticker.lat))
    && Number.isFinite(Number(sticker.scale))
    && Number(sticker.scale) > 0
    && Number.isFinite(Number(sticker.rotationRad))
    && Number.isFinite(Number(sticker.srcX))
    && Number.isFinite(Number(sticker.srcY))
    && sticker.srcBounds
    && Boolean(stickerSrcCorners(sticker));
}

/**
 * Similitud metros DXF → WGS84 (escala uniforme + rotación + traslado).
 * `scale`: metros de mapa por metro de CAD. `rotationRad`: 0 = +X al este.
 *
 * @param {{
 *   srcBounds: { minX: number, minY: number, maxX: number, maxY: number },
 *   lng: number,
 *   lat: number,
 *   scale?: number,
 *   rotationRad?: number,
 *   baseScale?: number,
 * }} input
 */
export function createCadGeorefSticker({
  srcBounds,
  lng,
  lat,
  scale = 1,
  rotationRad = 0,
  baseScale,
  srcCorners,
} = {}) {
  if (!srcBounds) return null;
  const srcX = (srcBounds.minX + srcBounds.maxX) / 2;
  const srcY = (srcBounds.minY + srcBounds.maxY) / 2;
  const nextScale = Number(scale);
  if (![srcX, srcY, lng, lat, nextScale].every(Number.isFinite) || nextScale <= 0) {
    return null;
  }
  const corners = parseCadGeorefSrcCorners(srcCorners) || srcCornersFromBounds(srcBounds);
  if (!corners) return null;
  const placed = Number.isFinite(Number(baseScale)) && Number(baseScale) > 0
    ? Number(baseScale)
    : nextScale;
  return {
    srcBounds,
    srcCorners: corners,
    srcX,
    srcY,
    lng: Number(lng),
    lat: Number(lat),
    scale: nextScale,
    rotationRad: Number(rotationRad) || 0,
    baseScale: placed,
    anchorLng: Number(lng),
    anchorLat: Number(lat),
  };
}

/**
 * Encaje proporcional (contain) del DXF en un bbox WGS84, sin rotar.
 * @param {object} srcBounds
 * @param {[number, number, number, number]} lotBbox minLng,minLat,maxLng,maxLat
 */
export function stickerFromLotBbox(srcBounds, lotBbox, srcCorners) {
  if (!srcBounds || !Array.isArray(lotBbox) || lotBbox.length < 4) return null;
  const minLng = Number(lotBbox[0]);
  const minLat = Number(lotBbox[1]);
  const maxLng = Number(lotBbox[2]);
  const maxLat = Number(lotBbox[3]);
  if (![minLng, minLat, maxLng, maxLat].every(Number.isFinite)) return null;
  const lng = (minLng + maxLng) / 2;
  const lat = (minLat + maxLat) / 2;
  const cadW = Math.abs(srcBounds.maxX - srcBounds.minX);
  const cadH = Math.abs(srcBounds.maxY - srcBounds.minY);
  if (!(cadW > 0) || !(cadH > 0)) return null;
  const lotW = Math.abs(maxLng - minLng) * metersPerDegreeLng(lat);
  const lotH = Math.abs(maxLat - minLat) * CAD_PLAN_METERS_PER_DEGREE;
  const scale = Math.min(lotW / cadW, lotH / cadH);
  if (!Number.isFinite(scale) || scale <= 0) return null;
  return createCadGeorefSticker({
    srcBounds,
    lng,
    lat,
    scale,
    rotationRad: 0,
    baseScale: scale,
    srcCorners,
  });
}

/**
 * Recupera la similitud guardada (`georef.points`: src metros + lnglat).
 * @param {object} srcBounds
 * @param {Array<{ src?: number[], lnglat?: number[] }>|null|undefined} points
 * @param {number[][]|null} [srcCorners]
 */
export function stickerFromGeorefPoints(srcBounds, points, srcCorners) {
  if (!srcBounds) return null;
  const pairs = (Array.isArray(points) ? points : [])
    .filter((point) => isCadSrcMetersPair(point?.src) && isFinitePair(point?.lnglat));
  if (pairs.length < 2) return null;
  const [p0, p1] = pairs;
  const dx = p1.src[0] - p0.src[0];
  const dy = p1.src[1] - p0.src[1];
  const srcLen = Math.hypot(dx, dy);
  if (!(srcLen > 1e-9)) return null;
  const lat0 = Number(p0.lnglat[1]);
  const east = (Number(p1.lnglat[0]) - Number(p0.lnglat[0])) * metersPerDegreeLng(lat0);
  const north = (Number(p1.lnglat[1]) - lat0) * CAD_PLAN_METERS_PER_DEGREE;
  const dstLen = Math.hypot(east, north);
  if (!(dstLen > 1e-12)) return null;
  const scale = dstLen / srcLen;
  const rotationRad = Math.atan2(north, east) - Math.atan2(dy, dx);
  const srcX = (srcBounds.minX + srcBounds.maxX) / 2;
  const srcY = (srcBounds.minY + srcBounds.maxY) / 2;
  const dx0 = p0.src[0] - srcX;
  const dy0 = p0.src[1] - srcY;
  const cos = Math.cos(rotationRad);
  const sin = Math.sin(rotationRad);
  const east0 = scale * (dx0 * cos - dy0 * sin);
  const north0 = scale * (dx0 * sin + dy0 * cos);
  const lat = lat0 - north0 / CAD_PLAN_METERS_PER_DEGREE;
  const lng = Number(p0.lnglat[0]) - east0 / metersPerDegreeLng(lat);
  return createCadGeorefSticker({
    srcBounds,
    lng,
    lat,
    scale,
    rotationRad,
    baseScale: scale,
    srcCorners,
  });
}

/**
 * Pose que explica el overlay WGS84 del GET (puntos guardados, o contain).
 * No cambia al hacer “Ajustar a lote madre”.
 * @param {object|null|undefined} project
 */
export function readCadGeorefOverlayOrigin(project) {
  const srcCorners = readCadGeorefSrcCorners(project);
  const srcBounds = readCadStickerSrcBounds(project);
  return stickerFromGeorefPoints(srcBounds, project?.georef?.points, srcCorners)
    || stickerFromWgs84Collection(srcBounds, project?.parcels, srcCorners);
}

/**
 * Apoyar el loteo sobre un overlay WGS84 ya pintado (contain, sin rotar).
 * @param {object} srcBounds
 * @param {object|null} featureCollection
 */
export function stickerFromWgs84Collection(srcBounds, featureCollection, srcCorners) {
  const bbox = computeFeatureCollectionBbox(featureCollection);
  return bbox ? stickerFromLotBbox(srcBounds, bbox, srcCorners) : null;
}

/**
 * @param {number} x
 * @param {number} y
 * @param {object} sticker
 * @returns {[number, number]|null}
 */
export function projectCadSrcWithSticker(x, y, sticker) {
  if (!isFiniteSticker(sticker)) return null;
  const dx = Number(x) - sticker.srcX;
  const dy = Number(y) - sticker.srcY;
  if (!Number.isFinite(dx) || !Number.isFinite(dy)) return null;
  const cos = Math.cos(sticker.rotationRad);
  const sin = Math.sin(sticker.rotationRad);
  const east = sticker.scale * (dx * cos - dy * sin);
  const north = sticker.scale * (dx * sin + dy * cos);
  return [
    sticker.lng + east / metersPerDegreeLng(sticker.lat),
    sticker.lat + north / CAD_PLAN_METERS_PER_DEGREE,
  ];
}

/**
 * @param {object} sticker
 * @param {number} dlng
 * @param {number} dlat
 */
export function translateCadGeorefSticker(sticker, dlng, dlat) {
  if (!isFiniteSticker(sticker)) return sticker;
  const lng = sticker.lng + Number(dlng);
  const lat = sticker.lat + Number(dlat);
  if (!Number.isFinite(lng) || !Number.isFinite(lat)) return sticker;
  return { ...sticker, lng, lat };
}

/**
 * @param {object} sticker
 * @param {number} scaleMul relativo a `baseScale` (1 = como se apoyó)
 */
export function scaleCadGeorefSticker(sticker, scaleMul) {
  if (!isFiniteSticker(sticker)) return sticker;
  const mul = Number(scaleMul);
  if (!Number.isFinite(mul) || mul <= 0) return sticker;
  return { ...sticker, scale: sticker.baseScale * mul };
}

/**
 * @param {object} sticker
 * @param {number} rotationRad
 */
export function rotateCadGeorefSticker(sticker, rotationRad) {
  if (!isFiniteSticker(sticker)) return sticker;
  const next = Number(rotationRad);
  if (!Number.isFinite(next)) return sticker;
  return { ...sticker, rotationRad: next };
}

export function cadGeorefStickerScaleMul(sticker) {
  if (!isFiniteSticker(sticker) || !(sticker.baseScale > 0)) return 1;
  return sticker.scale / sticker.baseScale;
}

export function cadGeorefStickerRotationDeg(sticker) {
  if (!isFiniteSticker(sticker)) return 0;
  return (sticker.rotationRad * 180) / Math.PI;
}

function nearlyEqual(a, b, eps) {
  return Math.abs(Number(a) - Number(b)) <= eps;
}

/**
 * True si el sticker ya es el encaje contain del lote madre (sin rotar).
 * @param {object|null|undefined} sticker
 * @param {object|null|undefined} srcBounds
 * @param {[number, number, number, number]|null|undefined} lotBbox
 * @returns {boolean}
 */
export function cadGeorefStickerMatchesLotSnap(sticker, srcBounds, lotBbox) {
  const snap = stickerFromLotBbox(srcBounds, lotBbox);
  if (!snap || !isFiniteSticker(sticker)) return false;
  const scaleEps = Math.max(1e-6, Number(snap.scale) * 1e-4);
  return nearlyEqual(sticker.lng, snap.lng, 1e-7)
    && nearlyEqual(sticker.lat, snap.lat, 1e-7)
    && nearlyEqual(sticker.scale, snap.scale, scaleEps)
    && nearlyEqual(sticker.rotationRad, snap.rotationRad, 1e-4);
}

/**
 * Habilitar “Ajustar a lote madre” solo si hay bbox y la pose no es ese encaje.
 * @param {{ sticker?: object|null, srcBounds?: object|null, lotBbox?: number[]|null }} input
 * @returns {boolean}
 */
export function canSnapCadGeorefStickerToLot({ sticker, srcBounds, lotBbox } = {}) {
  if (!stickerFromLotBbox(srcBounds, lotBbox) || !isFiniteSticker(sticker)) return false;
  return !cadGeorefStickerMatchesLotSnap(sticker, srcBounds, lotBbox);
}

/**
 * Preview en el mapa país. Metros DXF → WGS84 con la similitud del sticker.
 * En modo map, el overlay se deshace con la georef *guardada* (origen fijo)
 * y se vuelve a proyectar con la pose actual. Así “Ajustar a lote madre”
 * vuelve al contain inicial, no al loteo cruzado ya persistido.
 *
 * @param {object|null|undefined} project
 * @param {object|null} sticker
 */
export function previewCadGeorefSticker(project, sticker) {
  if (!isFiniteSticker(sticker)) return null;
  const meters = readCadMeterCollection(project);
  const source = meters || project?.parcels;
  if (!source) return null;
  if (meters) {
    return mapCadFeatureCollection(source, (x, y) => (
      projectCadSrcWithSticker(x, y, sticker) || [x, y]
    ));
  }
  const origin = readCadGeorefOverlayOrigin(project);
  if (!origin) return normalizeCadFeatureCollection(source);
  return mapCadFeatureCollection(source, (lng, lat) => {
    const src = unprojectCadLngLatWithSticker(lng, lat, origin);
    if (!src) return [lng, lat];
    return projectCadSrcWithSticker(src[0], src[1], sticker) || [lng, lat];
  });
}

/**
 * Inversa de `projectCadSrcWithSticker`: mapa → metros del DXF.
 * @param {number} lng
 * @param {number} lat
 * @param {object|null} sticker
 * @returns {number[]|null}
 */
export function unprojectCadLngLatWithSticker(lng, lat, sticker) {
  if (!isFiniteSticker(sticker) || !(sticker.scale > 0)) return null;
  const east = (Number(lng) - sticker.lng) * metersPerDegreeLng(sticker.lat);
  const north = (Number(lat) - sticker.lat) * CAD_PLAN_METERS_PER_DEGREE;
  if (!Number.isFinite(east) || !Number.isFinite(north)) return null;
  const cos = Math.cos(sticker.rotationRad);
  const sin = Math.sin(sticker.rotationRad);
  const dx = (east * cos + north * sin) / sticker.scale;
  const dy = (-east * sin + north * cos) / sticker.scale;
  return [sticker.srcX + dx, sticker.srcY + dy];
}

/**
 * 3 pares para el PATCH. `src` = esquinas fijas del DXF (metros).
 * `lnglat` = esas esquinas en el mapa tras el ajuste. No usar overlay WGS84
 * como src: el API responde 400 (no cae en Argentina).
 * @param {object|null} sticker
 * @returns {{ src: number[], lnglat: number[] }[]}
 */
export function georefPointsFromSticker(sticker) {
  if (!isFiniteSticker(sticker)) return [];
  const corners = stickerSrcCorners(sticker);
  if (!corners) return [];
  const points = corners.map((src) => {
    const lnglat = projectCadSrcWithSticker(src[0], src[1], sticker);
    if (!lnglat) return null;
    return { src: [src[0], src[1]], lnglat };
  }).filter(Boolean);
  return points.length >= CAD_GEOREF_MIN_POINTS ? points : [];
}

/**
 * Bbox CAD en metros. GET `georef_src_corners` / puntos src; nunca parcels
 * de modo map (`geom_4326`).
 * @param {object|null|undefined} project
 */
export function readCadStickerSrcBounds(project) {
  return srcBoundsFromCadSrcCorners(readCadGeorefSrcCorners(project));
}
